import {useCallback, useContext, useEffect, useRef} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {EventCreateStickerContext} from '~/CreateFabricInvitation/routes/EventCreateDesign/components/Sticker/EventCreateStickerContext';
import {UPDATE_DESIGN_OPTIONS} from '~/CreateFabricInvitation/reducers/fabricDesign/constants';
import {selectEnvelopeStickers} from '~/CreateFabricInvitation/reducers/fabricDesign/selectors';
import fabric from '~/common/canvas/fabric';
import {useStickerCanvas} from '~/common/canvas/hooks/useStickerCanvas';

export const StickersCanvas = ({className}) => {
  const {canvas, setCanvas} = useContext(EventCreateStickerContext);
  const designEnvelopeStickers = useSelector(selectEnvelopeStickers);
  const dispatch = useDispatch();
  const shiftPressed = useRef(false);

  const onChangeHandler = useCallback(
    (envelopeStickers) => {
      dispatch({
        type: UPDATE_DESIGN_OPTIONS,
        payload: {
          envelopeStickers,
        },
      });
    },
    [dispatch]
  );
  const {canvasRef} = useStickerCanvas({
    configJson: designEnvelopeStickers,
    onChange: onChangeHandler,
    setCanvas,
  });

  const BASE_STEP = 1;
  const SHIFT_STEP = 10;

  const Direction = {
    LEFT: 0,
    UP: 1,
    RIGHT: 2,
    DOWN: 3,
  };

  const moveSelected = useCallback(
    (direction) => {
      const activeObject = canvas?.getActiveObject();
      const stepLength = shiftPressed.current ? SHIFT_STEP : BASE_STEP;

      if (activeObject) {
        switch (direction) {
          case Direction.LEFT:
            activeObject.left -= stepLength;
            break;
          case Direction.UP:
            activeObject.top -= stepLength;
            break;
          case Direction.RIGHT:
            activeObject.left += stepLength;
            break;
          case Direction.DOWN:
            activeObject.top += stepLength;
            break;
          default:
            break;
        }
        activeObject.setCoords();
        canvas?.renderAll();
        onChangeHandler(canvas?.toJSON(['dataUrl', 'delay', 'frameWidth', 'framesLength', 'id']));
      }
    },
    [Direction.DOWN, Direction.LEFT, Direction.RIGHT, Direction.UP, canvas, onChangeHandler]
  );

  useEffect(() => {
    const onKeyDown = (options) => {
      if (options.repeat) {
        return;
      }
      const key = options.which || options.keyCode; // key detection
      if (key === 16) {
        // handle Shift key
        shiftPressed.current = true;
      }
      if (key === 37) {
        // handle Left key
        moveSelected(Direction.LEFT);
      } else if (key === 38) {
        // handle Up key
        moveSelected(Direction.UP);
      } else if (key === 39) {
        // handle Right key
        moveSelected(Direction.RIGHT);
      } else if (key === 40) {
        // handle Down key
        moveSelected(Direction.DOWN);
      }
    };

    const onKeyUp = (options) => {
      if (options.repeat) {
        return;
      }
      const key = options.which || options.keyCode; // key detection
      if (key === 16) {
        // handle Shift key
        shiftPressed.current = false;
      }
    };

    fabric.util.addListener(document.body, 'keydown', onKeyDown);
    fabric.util.addListener(document.body, 'keyup', onKeyUp);

    return () => {
      fabric.util.removeListener(document.body, 'keydown', onKeyDown);
      fabric.util.removeListener(document.body, 'keyup', onKeyUp);
    };
  }, [Direction.DOWN, Direction.LEFT, Direction.RIGHT, Direction.UP, canvas, moveSelected]);

  return (
    <div className={className}>
      <canvas ref={canvasRef} />
    </div>
  );
};
